import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import axios from 'axios';
import config from '../config';

interface Theme {
  colors: {
    primary: string;
    primaryDark: string;
    secondary: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    shadow: string;
    gradient: string;
    cardBackground: string;
    success: string;
    warning: string;
    error: string;
  };
  breakpoints: {
    mobile: string;
    tablet: string;
    desktop: string;
    wide: string;
  };
}

const createLightTheme = (primaryColor: string, secondaryColor: string): Theme => ({
  colors: {
    primary: primaryColor,
    primaryDark: '#4f46e5',
    secondary: secondaryColor,
    background: '#ffffff',
    surface: '#f8fafc',
    text: '#1e293b',
    textSecondary: '#64748b',
    border: '#e2e8f0',
    shadow: 'rgba(0, 0, 0, 0.1)',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    cardBackground: '#ffffff',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px',
    wide: '1440px'
  },
});

const createDarkTheme = (primaryColor: string, secondaryColor: string): Theme => ({
  colors: {
    primary: primaryColor,
    primaryDark: '#4f46e5',
    secondary: secondaryColor,
    background: '#0f172a',
    surface: '#1e293b',
    text: '#f1f5f9',
    textSecondary: '#94a3b8',
    border: '#334155',
    shadow: 'rgba(0, 0, 0, 0.3)',
    gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
    cardBackground: '#1e293b',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px',
    wide: '1440px'
  },
});

interface ThemeContextType {
  theme: Theme;
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState(false);
  const [appearanceSettings, setAppearanceSettings] = useState({
    primary_color: '#6366f1',
    secondary_color: '#8b5cf6'
  });

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setIsDark(savedTheme === 'dark');
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setIsDark(prefersDark);
    }
  }, []);

  useEffect(() => {
    const fetchAppearanceSettings = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/public-appearance-settings.php`);
        if (response.data.success) {
          setAppearanceSettings({
            primary_color: response.data.data.primary_color || '#6366f1',
            secondary_color: response.data.data.secondary_color || '#8b5cf6'
          });
        }
      } catch (error) {
        console.error('Error fetching appearance settings:', error);
      }
    };

    fetchAppearanceSettings();
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem('theme', newTheme ? 'dark' : 'light');
    document.body.setAttribute('data-theme', newTheme ? 'dark' : 'light');
  };

  const theme = isDark 
    ? createDarkTheme(appearanceSettings.primary_color, appearanceSettings.secondary_color)
    : createLightTheme(appearanceSettings.primary_color, appearanceSettings.secondary_color);

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      <StyledThemeProvider theme={theme}>
        {children}
      </StyledThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;